import {
  BuildingOfficeIcon,
  CheckIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

import {
  CATEGORY_FILTER_OPTIONS,
  CATEGORY_META,
} from '@/components/templates-v2/constants';
import { TemplatePreview } from '@/components/templates-v2/document-generation/templates/TemplatePreview';
import { Button } from '@/components/ui/catalyst/button';
import { Dialog, DialogTitle } from '@/components/ui/catalyst/dialog';
import { cn } from '@/lib/utils';
import type { TemplateCategory , TemplateFormat } from '@/services/api/generated/templatesV2Service';

import {
  type SourceFilter,
  type TemplateItem,
  type TemplateType,
  useTemplateSearch,
} from '../hooks/useTemplateSearch';

const FORMAT_BADGE_COLORS: Record<TemplateFormat, string> = {
  PDF: 'bg-rose-50 text-rose-600',
  DOCX: 'bg-sky-50 text-sky-600',
  HTML: 'bg-orange-50 text-orange-600',
  SMS: 'bg-green-50 text-green-600',
  EXTERNAL: 'bg-purple-50 text-purple-600',
};

interface TemplateSearchListProps {
  matterId: string;
  onSelectTemplate: (templateId: string) => void;
  isLoading?: boolean;
  includeCategories?: string[];
  /** Filter to only show templates of specific types (e.g., ['PDF'] for merge documents) */
  includeTemplateTypes?: TemplateType[];
  enablePreview?: boolean;
  autoConfirmOnSelect?: boolean;
  confirmButtonLabel?: string;
}

const SOURCE_FILTER_CONFIG: {
  value: SourceFilter;
  label: string;
  icon: typeof BuildingOfficeIcon;
}[] = [
  { value: 'firm', label: 'Firm', icon: BuildingOfficeIcon },
  { value: 'global', label: 'Global', icon: GlobeAltIcon },
];

export const TemplateSearchList: React.FC<TemplateSearchListProps> = ({
  matterId,
  onSelectTemplate,
  isLoading: externalLoading = false,
  includeCategories,
  includeTemplateTypes,
  enablePreview = false,
  autoConfirmOnSelect = false,
  confirmButtonLabel = 'Add Template',
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(
    null,
  );

  const {
    templates,
    isLoading: hookLoading,
    sourceFilter,
    setSourceFilter,
    searchQuery,
    setSearchQuery,
    categoryFilter,
    setCategoryFilter,
    hasFirmTemplates,
    hasGlobalTemplates,
    availableCategories,
  } = useTemplateSearch({
    matterId,
    includeCategories,
    includeTemplateTypes,
  });

  const isLoading = hookLoading || externalLoading;
  const showSourceFilter = hasFirmTemplates && hasGlobalTemplates;
  const showCategoryFilter =
    !includeCategories && availableCategories.length > 1;

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (autoConfirmOnSelect) {
      onSelectTemplate(templateId);
    }
  };

  const handleConfirm = () => {
    if (selectedTemplateId) {
      onSelectTemplate(selectedTemplateId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="size-5 animate-spin rounded-full border-2 border-zinc-200 border-t-indigo-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Filters */}
      <div className="space-y-2 mb-3">
        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-10 pl-10 pr-4 text-sm bg-zinc-50 border-0 rounded-xl placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition-all"
          />
        </div>

        {/* Source filter */}
        {showSourceFilter && (
          <div className="flex gap-0.5 p-0.5 bg-zinc-100/80 rounded-lg w-fit">
            {SOURCE_FILTER_CONFIG.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => setSourceFilter(value)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                  sourceFilter === value
                    ? 'bg-white text-zinc-900 shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-700',
                )}
              >
                <Icon className="size-3.5" />
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Category filter */}
        {showCategoryFilter && (
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span>Category:</span>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="bg-transparent border-0 text-xs font-medium text-zinc-700 focus:outline-none cursor-pointer pr-5 -ml-1"
            >
              {CATEGORY_FILTER_OPTIONS.filter(
                (opt) =>
                  opt.value === 'all' ||
                  availableCategories.includes(opt.value),
              ).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Template list */}
      <div className="max-h-80 overflow-y-auto -mx-1 px-1">
        {templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <DocumentTextIcon className="size-8 text-zinc-200 mb-2" />
            <p className="text-sm text-zinc-500">
              {searchQuery || categoryFilter !== 'all'
                ? 'No matches found'
                : 'No templates available'}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {templates.map((template) => (
              <TemplateListItem
                key={template.id}
                template={template}
                isSelected={selectedTemplateId === template.id}
                onSelect={() => handleTemplateSelect(template.id)}
                enablePreview={enablePreview}
                onPreview={() => setPreviewTemplateId(template.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Confirm button */}
      {!autoConfirmOnSelect && selectedTemplateId && (
        <div className="pt-3 mt-3 border-t border-zinc-100">
          <Button
            onClick={handleConfirm}
            color="indigo"
            className="w-full shadow-sm"
          >
            {confirmButtonLabel}
          </Button>
        </div>
      )}

      {/* Preview Dialog */}
      {enablePreview && previewTemplateId && (
        <Dialog onClose={() => setPreviewTemplateId(null)} open>
          <DialogTitle>Template Preview</DialogTitle>
          <div className="mt-4 h-[70vh] overflow-hidden">
            <TemplatePreview
              templateId={previewTemplateId}
              matterId={matterId}
              overrides={{}}
            />
          </div>
        </Dialog>
      )}
    </div>
  );
};

interface TemplateListItemProps {
  template: TemplateItem;
  isSelected: boolean;
  onSelect: () => void;
  enablePreview?: boolean;
  onPreview?: () => void;
}

// Color classes for category badges
const CATEGORY_COLORS: Record<string, string> = {
  blue: 'bg-blue-50 text-blue-700',
  purple: 'bg-purple-50 text-purple-700',
  green: 'bg-green-50 text-green-700',
  amber: 'bg-amber-50 text-amber-700',
  rose: 'bg-rose-50 text-rose-700',
  zinc: 'bg-zinc-100 text-zinc-600',
};

const TemplateListItem: React.FC<TemplateListItemProps> = ({
  template,
  isSelected,
  onSelect,
  enablePreview,
  onPreview,
}) => {
  const displayName = template.display_name || template.name || 'Untitled';
  const hasName = !!(template.display_name || template.name);
  const categoryMeta =
    CATEGORY_META[template.category as TemplateCategory];
  const categoryColor = categoryMeta
    ? CATEGORY_COLORS[categoryMeta.color]
    : CATEGORY_COLORS.zinc;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full px-3 py-2.5 rounded-xl text-left transition-all group',
        isSelected
          ? 'bg-indigo-50 ring-1 ring-indigo-200'
          : 'hover:bg-zinc-50 active:bg-zinc-100',
      )}
    >
      <div className="flex items-center gap-3">
        {/* Selection indicator */}
        <div
          className={cn(
            'size-5 rounded-full flex items-center justify-center shrink-0 transition-all',
            isSelected
              ? 'bg-indigo-600'
              : 'border-2 border-zinc-200 group-hover:border-zinc-300',
          )}
        >
          {isSelected && <CheckIcon className="size-3 text-white" />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <span
            className={cn(
              'text-xs font-medium truncate block leading-snug',
              isSelected ? 'text-indigo-900' : 'text-zinc-800',
              !hasName && 'italic text-zinc-500',
            )}
          >
            {displayName}
          </span>
          <div className="flex items-center gap-1.5 mt-1">
            <span
              className={cn(
                'inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded',
                isSelected ? 'bg-indigo-100 text-indigo-700' : categoryColor,
              )}
            >
              {categoryMeta?.shortLabel ?? template.category}
            </span>
            <span
              className={cn(
                'inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded',
                FORMAT_BADGE_COLORS[template.format] ?? 'bg-sky-50 text-sky-600',
              )}
            >
              {template.format}
            </span>
            {template.is_default && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 rounded">
                <StarIcon className="size-2.5 fill-amber-400 text-amber-400" />
                Default
              </span>
            )}
            {!template.is_firm_template && (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 text-[10px]',
                  isSelected ? 'text-indigo-500' : 'text-zinc-400',
                )}
              >
                <GlobeAltIcon className="size-3" />
                Global
              </span>
            )}
          </div>
        </div>

        {/* Preview button */}
        {enablePreview && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPreview?.();
            }}
            className={cn(
              'text-xs font-medium px-2 py-1 rounded-lg transition-all shrink-0',
              'opacity-0 group-hover:opacity-100',
              isSelected
                ? 'text-indigo-600 hover:bg-indigo-100'
                : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100',
            )}
          >
            Preview
          </button>
        )}
      </div>
    </button>
  );
};
